import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { format, subMonths, addMonths } from 'date-fns';
import { ru } from 'date-fns/locale';
import { getAllActiveFactories, getAllClusters, getFinanceAverageRevenueWithAllFactories, createFinanceAverageRevenue, updateFinanceAverageRevenue } from '../../api';
import { colorOptionsFind } from '../references/ColorOptions';
import './FinancePage.css';

const FinancePage = () => {
    const [clusters, setClusters] = useState([]);
    const [factories, setFactories] = useState([]);
    const [financeData, setFinanceData] = useState({});
    const [editingCell, setEditingCell] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [baseMonth, setBaseMonth] = useState(new Date()); // Базовый месяц для отображения

    // Функция определения типа месяца относительно реальной текущей даты
    const getMonthType = useCallback((monthDate, realCurrentDate) => {
        const monthYear = monthDate.getFullYear();
        const month = monthDate.getMonth();
        const currentYear = realCurrentDate.getFullYear();
        const currentMonth = realCurrentDate.getMonth();
        
        if (monthYear < currentYear || (monthYear === currentYear && month < currentMonth)) {
            return 'past';
        } else if (monthYear === currentYear && month === currentMonth) {
            return 'current';
        } else {
            return 'future';
        }
    }, []);

    const monthHeaders = useMemo(() => {
        const months = [];
        const currentDate = baseMonth; // Используем baseMonth для центра периода
        const realCurrentDate = new Date(); // Реальная текущая дата для определения типов
        
        // 3 месяца до базового месяца
        for (let i = 3; i >= 1; i--) {
            const date = subMonths(currentDate, i);
            months.push({
                date,
                type: getMonthType(date, realCurrentDate)
            });
        }
        
        // Базовый месяц
        months.push({
            date: currentDate,
            type: getMonthType(currentDate, realCurrentDate)
        });
        
        // 3 месяца после базового месяца
        for (let i = 1; i <= 3; i++) {
            const date = addMonths(currentDate, i);
            months.push({
                date,
                type: getMonthType(date, realCurrentDate)
            });
        }
        
        return months;
    }, [baseMonth, getMonthType]); // Зависимость от baseMonth и getMonthType

    // Функции для смещения периода
    const shiftPeriodLeft = () => {
        setBaseMonth(prev => subMonths(prev, 2));
    };

    const shiftPeriodRight = () => {
        setBaseMonth(prev => addMonths(prev, 2));
    };

    // Загружаем кластеры и заводы только один раз
    useEffect(() => {
        const loadBasicData = async () => {
            try {
                const [clustersResponse, factoriesResponse] = await Promise.all([
                    getAllClusters(),
                    getAllActiveFactories() // Используем только активные заводы
                ]);

                setClusters(clustersResponse.data);
                setFactories(factoriesResponse.data);
            } catch (error) {
                console.error('Ошибка при загрузке базовых данных:', error);
                setClusters([]);
                setFactories([]);
            }
        };

        loadBasicData();
    }, []);

    // Загружаем финансовые данные при изменении monthHeaders
    useEffect(() => {
        if (!factories.length) return; // Не загружаем данные, если заводы еще не загружены

        const loadFinanceData = async () => {
            try {
                setLoading(true);
                const factoryIds = factories.map(f => f.id);
                const financeDataMap = {};
                
                // Загружаем данные для каждого месяца
                for (const monthHeader of monthHeaders) {
                    const monthDate = monthHeader.date;
                    const month = monthDate.getMonth() + 1; // getMonth() возвращает 0-11
                    const year = monthDate.getFullYear();
                    
                    try {
                        const response = await getFinanceAverageRevenueWithAllFactories(factoryIds, month, year);
                        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
                        
                        // Преобразуем массив в объект для быстрого доступа
                        const monthFinanceData = {};
                        response.data.forEach(item => {
                            const key = `${item.factory.id}`;
                            monthFinanceData[key] = item;
                        });
                        
                        financeDataMap[monthKey] = monthFinanceData;
                    } catch (error) {
                        console.warn(`Не удалось загрузить данные за ${month}/${year}:`, error);
                        // Продолжаем с другими месяцами
                    }
                }
                
                setFinanceData(financeDataMap);
            } catch (error) {
                console.error('Ошибка при загрузке финансовых данных:', error);
            } finally {
                setLoading(false);
            }
        };

        loadFinanceData();
    }, [monthHeaders, factories]);

    // Обработчики для редактирования
    const handleCellClick = (factoryId, monthHeader) => {
        const monthDate = monthHeader.date;
        const month = monthDate.getMonth() + 1;
        const year = monthDate.getFullYear();
        const cellKey = `${factoryId}-${year}-${month.toString().padStart(2, '0')}`;
        
        setEditingCell(cellKey);
    };

    const handleCellChange = (factoryId, monthHeader, value) => {
        const monthDate = monthHeader.date;
        const month = monthDate.getMonth() + 1;
        const year = monthDate.getFullYear();
        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
        
        setFinanceData(prev => ({
            ...prev,
            [monthKey]: {
                ...prev[monthKey],
                [factoryId]: {
                    ...prev[monthKey]?.[factoryId],
                    averageRevenue: parseFloat(value) || 0
                }
            }
        }));
    };

    const handleCellBlur = async (factoryId, monthHeader) => {
        if (saving) return;
        
        const monthDate = monthHeader.date;
        const month = monthDate.getMonth() + 1;
        const year = monthDate.getFullYear();
        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
        
        const cellData = financeData[monthKey]?.[factoryId];
        if (!cellData) return;
        
        try {
            setSaving(true);
            
            const financeRecord = {
                factory: { id: factoryId },
                month,
                year,
                averageRevenue: cellData.averageRevenue
            };
            
            if (cellData.id) {
                // Обновляем существующую запись
                await updateFinanceAverageRevenue(cellData.id, financeRecord);
            } else {
                // Создаем новую запись
                const response = await createFinanceAverageRevenue(financeRecord);
                // Обновляем ID в состоянии
                setFinanceData(prev => ({
                    ...prev,
                    [monthKey]: {
                        ...prev[monthKey],
                        [factoryId]: {
                            ...prev[monthKey][factoryId],
                            id: response.data.id
                        }
                    }
                }));
            }
        } catch (error) {
            console.error('Ошибка при сохранении данных:', error);
            // Можно добавить уведомление об ошибке
        } finally {
            setSaving(false);
            setEditingCell(null);
        }
    };

    const handleKeyDown = (e, factoryId, monthHeader) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleCellBlur(factoryId, monthHeader);
        }
        if (e.key === 'Escape') {
            setEditingCell(null);
        }
    };

    // Получение значения для ячейки
    const getCellValue = (factoryId, monthHeader) => {
        const monthDate = monthHeader.date;
        const month = monthDate.getMonth() + 1;
        const year = monthDate.getFullYear();
        const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
        
        return financeData[monthKey]?.[factoryId]?.averageRevenue || '';
    };

    // Расчет итогов по месяцам
    const calculateTotals = () => {
        const totals = {};
        
        monthHeaders.forEach(monthHeader => {
            const monthDate = monthHeader.date;
            const month = monthDate.getMonth() + 1;
            const year = monthDate.getFullYear();
            const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
            
            let sum = 0;
            let count = 0;
            
            factories.forEach(factory => {
                const value = financeData[monthKey]?.[factory.id]?.averageRevenue;
                if (value !== undefined && value !== null && value !== '') {
                    sum += parseFloat(value);
                    count++;
                }
            });
            
            totals[monthKey] = {
                sum: sum,
                average: count > 0 ? sum / count : 0,
                count: count
            };
        });
        
        return totals;
    };

    const totals = calculateTotals();

    if (loading) {
        return <div className="finance-page-loading">Загрузка данных...</div>;
    }

    // Группируем заводы по кластерам
    const factoriesByCluster = clusters.reduce((acc, cluster) => {
        acc[cluster.id] = factories.filter(factory => factory.cluster?.id === cluster.id);
        return acc;
    }, {});

    // Сортируем кластеры по порядку (если есть) или по имени
    const sortedClusters = [...clusters].sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
            return a.order - b.order;
        }
        return a.name.localeCompare(b.name);
    });

    return (
        <div className="finance-page-container">
            <div className="finance-page-header">
                <button 
                    className="finance-period-arrow finance-arrow-left" 
                    onClick={shiftPeriodLeft}
                    title="Сместить на 2 месяца назад"
                >
                    ◀
                </button>
                <div className="finance-title-container">
                    <h1>Финансы</h1>
                    {saving && <span className="finance-saving-indicator">Сохранение...</span>}
                </div>
                <button 
                    className="finance-period-arrow finance-arrow-right" 
                    onClick={shiftPeriodRight}
                    title="Сместить на 2 месяца вперед"
                >
                    ▶
                </button>
            </div>
            
            <div className="finance-description-container">
                <div className="finance-description">
                    В таблице заполняется средняя выручка за 1 человека в день для каждого месяца и завода
                </div>
            </div>
            
            <div className="finance-table-container">
                <table className="finance-table">
                    <thead>
                        <tr>
                            <th className="td_idx"></th>
                            <th className="td_idx">Кластер</th>
                            <th className="td_factory">Наименование предприятия</th>
                            {monthHeaders.map((monthHeader, index) => (
                                <th key={index} className={`th-month ${monthHeader.type}-month`}>
                                    {format(monthHeader.date, 'MMMM yyyy', { locale: ru })}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {(() => {
                            let globalIndex = 0;
                            return sortedClusters.map(cluster => {
                                const clusterFactories = factoriesByCluster[cluster.id] || [];
                                
                                // Если в кластере нет заводов, не отображаем его
                                if (clusterFactories.length === 0) {
                                    return null;
                                }
                                
                                return clusterFactories.map((factory, factoryIndex) => {
                                    globalIndex++;
                                    
                                    return (
                                        <tr key={`${cluster.id}-${factory.id}`} className="finance-row">
                                            <td className="td_idx" style={{backgroundColor: `${colorOptionsFind(cluster.color)}`}}>{globalIndex}</td>
                                            <td className="td_idx">
                                                {cluster.name}
                                            </td>
                                            <td className="td_factory">
                                                <div className="td_factory_name">{factory.name}</div>
                                            </td>
                                            {monthHeaders.map((monthHeader, monthIndex) => {
                                                const cellKey = `${factory.id}-${monthHeader.date.getFullYear()}-${(monthHeader.date.getMonth() + 1).toString().padStart(2, '0')}`;
                                                const isEditing = editingCell === cellKey;
                                                const cellValue = getCellValue(factory.id, monthHeader);
                                                
                                                return (
                                                    <td key={monthIndex} className={`td-finance-cell ${monthHeader.type}-month`}>
                                                        {isEditing ? (
                                                            <input
                                                                type="number"
                                                                step="0.01"
                                                                value={cellValue}
                                                                onChange={(e) => handleCellChange(factory.id, monthHeader, e.target.value)}
                                                                onBlur={() => handleCellBlur(factory.id, monthHeader)}
                                                                onKeyDown={(e) => handleKeyDown(e, factory.id, monthHeader)}
                                                                className="finance-cell-input"
                                                                autoFocus
                                                            />
                                                        ) : (
                                                            <div 
                                                                className="finance-cell-content"
                                                                onClick={() => handleCellClick(factory.id, monthHeader)}
                                                            >
                                                                {cellValue ? cellValue.toLocaleString('ru-RU', { minimumFractionDigits: 2 }) : ''}
                                                            </div>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                });
                            });
                        })()}
                    </tbody>
                    <tfoot>
                        <tr className="finance-totals-row">
                            <td className="td_idx" colSpan="2" style={{backgroundColor: '#f1f1f1', fontWeight: 'bold'}}>
                                Итого
                            </td>
                            <td className="td_factory" style={{backgroundColor: '#f1f1f1', fontWeight: 'bold'}}>
                                {factories.length} заводов
                            </td>
                            {monthHeaders.map((monthHeader, monthIndex) => {
                                const monthDate = monthHeader.date;
                                const month = monthDate.getMonth() + 1;
                                const year = monthDate.getFullYear();
                                const monthKey = `${year}-${month.toString().padStart(2, '0')}`;
                                const monthTotals = totals[monthKey];
                                
                                return (
                                    <td key={monthIndex} className={`td-finance-cell ${monthHeader.type}-month`} style={{backgroundColor: '#f1f1f1', fontWeight: 'bold', fontSize: '11px'}}>
                                        <div style={{marginBottom: '2px'}}>
                                            Сумма: {monthTotals.sum.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}
                                        </div>
                                        <div style={{fontSize: '10px', color: '#666'}}>
                                            Среднее: {monthTotals.average.toLocaleString('ru-RU', { minimumFractionDigits: 2 })}
                                        </div>
                                        <div style={{fontSize: '9px', color: '#999'}}>
                                            ({monthTotals.count})
                                        </div>
                                    </td>
                                );
                            })}
                        </tr>
                    </tfoot>
                </table>
                
                {sortedClusters.length === 0 || factories.length === 0 ? (
                    <div className="finance-empty-state">
                        <p>Нет данных для отображения</p>
                    </div>
                ) : null}
            </div>
        </div>
    );
};

export default FinancePage;
